/**
 * jsonImport.ts — Leitura e normalização de documentos JSON do Studio
 *
 * Este arquivo é a porta de entrada de qualquer conteúdo em JSON colado no
 * editor (normalmente gerado por uma LLM a partir do catálogo oficial de
 * blocos). Ele NÃO cria uma segunda validação: reaproveita o catálogo de blocos
 * (`blockRegistry`) e a validação estrutural (`validateDocument`) do Studio Core.
 *
 * Princípios:
 *   1. nada é descartado em silêncio — cada ajuste vira um item de relatório;
 *   2. correções automáticas SEGURAS (id refeito, propriedade desconhecida
 *      removida, valor de lista inválido trocado pelo padrão) são separadas de
 *      PERDAS ESTRUTURAIS (bloco desconhecido, filho em bloco que não aceita);
 *   3. ao refazer identificadores, o mapa `id antigo → id novo` é aplicado em
 *      todas as referências internas (âncoras, menus, alvos de formulário);
 *   4. limites operacionais protegem o renderizador contra árvores gigantes.
 *
 * O formato aceito já contempla o futuro `studio-fragment` (uma seção solta),
 * mesmo que a tela para inseri-lo isoladamente ainda não exista.
 *
 * RELEASE: v1.0.1 - 2026-09-11 - Corrigido erro que impedia QUALQUER leitura de
 *          JSON ("Cannot access 'bruto' before initialization"): a variável de
 *          metadados de composição tinha o mesmo nome do item da repetição.
 * RELEASE: v1.0.0 - 2026-09-10 - Versão inicial (importação de JSON no Studio)
 */

import {
  STUDIO_SCHEMA_VERSION,
  getBlock,
  validateDocument,
  type StudioBlockNode,
  type StudioDocument,
  type StudioTemplateType,
} from '@/lib/studio/core';

// =============================================================================
// Limites operacionais
// =============================================================================

/** Limites aplicados a qualquer conteúdo colado na aba JSON. */
export const STUDIO_JSON_LIMITS = {
  /** Tamanho do texto colado (1 MB). */
  maxPayloadBytes: 1024 * 1024,
  /** Quantidade total de blocos na árvore. */
  maxBlocks: 500,
  /** Profundidade máxima da árvore. */
  maxDepth: 8,
  /** Imagens embutidas (`data:`) aceitas por importação. */
  maxInlineImages: 30,
  /** Tamanho máximo de cada imagem embutida (5 MB já decodificados). */
  maxInlineImageBytes: 5 * 1024 * 1024,
} as const;

// =============================================================================
// Contratos
// =============================================================================

/** Natureza do conteúdo colado. */
export type StudioImportKind = 'document' | 'fragment';

/** Item do relatório de importação. */
export interface StudioImportNote {
  /** `fix` = correção automática segura; `loss` = perda estrutural. */
  severity: 'fix' | 'loss';
  message: string;
  blockId?: string;
}

/** Quem gerou o arquivo (somente auditoria). */
export interface StudioImportGenerator {
  name?: string;
  version?: string;
}

export interface StudioJsonImportResult {
  kind: StudioImportKind;
  /** Documento já normalizado — é exatamente o que será gravado. */
  document: StudioDocument;
  /** Tipo declarado no arquivo, quando informado. */
  documentType?: StudioTemplateType;
  generator?: StudioImportGenerator;
  /** Blocos aproveitados (contagem na árvore final). */
  validBlocks: number;
  /** Correções automáticas seguras. */
  fixes: StudioImportNote[];
  /** Blocos/estruturas que serão perdidos se o usuário insistir. */
  losses: StudioImportNote[];
  /** Mapa `id antigo → id novo` aplicado às referências internas. */
  idMap: Record<string, string>;
}

/** Erro de leitura que impede qualquer aproveitamento do conteúdo. */
export class StudioJsonImportError extends Error {}

// =============================================================================
// Utilidades internas
// =============================================================================

let contador = 0;

/** Gera um identificador novo e estável dentro da sessão. */
function novoId(tipo: string): string {
  contador += 1;
  const semNamespace = tipo.split('.').pop() ?? 'bloco';
  return `${semNamespace}-${Date.now().toString(36)}-${contador.toString(36)}`;
}

/** Converte valores simples para o tipo esperado pela propriedade. */
function coagirValor(tipo: string, valor: unknown): unknown {
  if (tipo === 'boolean') {
    if (typeof valor === 'boolean') return valor;
    if (valor === 'true') return true;
    if (valor === 'false') return false;
    return undefined;
  }
  if (tipo === 'number') {
    if (typeof valor === 'number' && Number.isFinite(valor)) return valor;
    const numero = Number(valor);
    return Number.isFinite(numero) ? numero : undefined;
  }
  if (typeof valor === 'string' || typeof valor === 'number' || typeof valor === 'boolean') {
    return String(valor);
  }
  // Objetos e listas são aceitos como estão (blocos com listas de itens).
  if (valor && typeof valor === 'object') return valor;
  return undefined;
}

/** Substitui identificadores antigos dentro de textos (âncoras, alvos). */
function reescreverReferencias(valor: unknown, mapa: Record<string, string>): unknown {
  if (typeof valor === 'string') {
    let saida = valor;
    for (const [antigo, novo] of Object.entries(mapa)) {
      if (!antigo || antigo === novo) continue;
      // `#ancora`, `ancora` isolado e `bloco:ancora` são todos cobertos.
      const escapado = antigo.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
      saida = saida.replace(new RegExp(`(^|[#:\\s])${escapado}(?=$|[\\s"'#?/])`, 'g'), `$1${novo}`);
    }
    return saida;
  }
  if (Array.isArray(valor)) return valor.map((item) => reescreverReferencias(item, mapa));
  if (valor && typeof valor === 'object') {
    const saida: Record<string, unknown> = {};
    for (const [chave, item] of Object.entries(valor as Record<string, unknown>)) {
      saida[chave] = reescreverReferencias(item, mapa);
    }
    return saida;
  }
  return valor;
}

/** Conta imagens embutidas (`data:`) presentes em qualquer texto do documento. */
export function contarImagensEmbutidas(valor: unknown): number {
  if (typeof valor === 'string') {
    return (valor.match(/data:image\/[a-z+]+;base64,/gi) ?? []).length;
  }
  if (Array.isArray(valor)) {
    return valor.reduce<number>((total, item) => total + contarImagensEmbutidas(item), 0);
  }
  if (valor && typeof valor === 'object') {
    return Object.values(valor as Record<string, unknown>).reduce<number>(
      (total, item) => total + contarImagensEmbutidas(item),
      0,
    );
  }
  return 0;
}

// =============================================================================
// Normalização da árvore
// =============================================================================

interface EstadoNormalizacao {
  fixes: StudioImportNote[];
  losses: StudioImportNote[];
  idMap: Record<string, string>;
  usados: Set<string>;
  total: number;
}

function normalizarNos(
  entrada: unknown,
  profundidade: number,
  estado: EstadoNormalizacao,
): StudioBlockNode[] {
  if (!Array.isArray(entrada)) return [];

  const saida: StudioBlockNode[] = [];

  for (const bruto of entrada) {
    if (!bruto || typeof bruto !== 'object') {
      estado.losses.push({ severity: 'loss', message: 'Item inválido na lista de blocos.' });
      continue;
    }

    const no = bruto as Partial<StudioBlockNode> & Record<string, unknown>;
    const tipo = String(no.type ?? '');
    const definicao = getBlock(tipo);

    if (!definicao) {
      estado.losses.push({
        severity: 'loss',
        message: `Bloco desconhecido e não aproveitável: ${tipo || '(sem tipo)'}.`,
        blockId: typeof no.id === 'string' ? no.id : undefined,
      });
      continue;
    }

    if (estado.total >= STUDIO_JSON_LIMITS.maxBlocks) {
      estado.losses.push({
        severity: 'loss',
        message: `Limite de ${STUDIO_JSON_LIMITS.maxBlocks} blocos atingido; o restante foi ignorado.`,
      });
      break;
    }

    if (profundidade > STUDIO_JSON_LIMITS.maxDepth) {
      estado.losses.push({
        severity: 'loss',
        message: `Bloco além da profundidade máxima (${STUDIO_JSON_LIMITS.maxDepth} níveis).`,
      });
      continue;
    }

    // ---------------------------------------------------------- Identificador
    const idOriginal = typeof no.id === 'string' ? no.id.trim() : '';
    let id = idOriginal;
    if (!id || estado.usados.has(id)) {
      id = novoId(tipo);
      if (idOriginal) {
        estado.idMap[idOriginal] = id;
        estado.fixes.push({
          severity: 'fix',
          message: `Identificador repetido "${idOriginal}" foi renomeado; as referências internas foram atualizadas.`,
          blockId: id,
        });
      } else {
        estado.fixes.push({
          severity: 'fix',
          message: `Bloco ${definicao.label} estava sem identificador; um novo foi gerado.`,
          blockId: id,
        });
      }
    }
    estado.usados.add(id);
    estado.total += 1;

    // ------------------------------------------------------------ Propriedades
    const propsEntrada = (no.props ?? {}) as Record<string, unknown>;
    const props: Record<string, unknown> = {};
    const conhecidas = new Set(definicao.props.map((p) => p.key));

    for (const definicaoProp of definicao.props) {
      const valorBruto = propsEntrada[definicaoProp.key];
      if (valorBruto === undefined || valorBruto === null) {
        if (definicaoProp.defaultValue !== undefined) props[definicaoProp.key] = definicaoProp.defaultValue;
        continue;
      }

      const coagido = coagirValor(definicaoProp.type, valorBruto);
      if (coagido === undefined) {
        props[definicaoProp.key] = definicaoProp.defaultValue;
        estado.fixes.push({
          severity: 'fix',
          message: `Valor inválido em "${definicaoProp.label}" (${definicao.label}); o padrão foi aplicado.`,
          blockId: id,
        });
        continue;
      }

      if (definicaoProp.type === 'select' && definicaoProp.options?.length) {
        const aceitos = definicaoProp.options.map((o) => o.value);
        if (!aceitos.includes(String(coagido))) {
          props[definicaoProp.key] = definicaoProp.defaultValue ?? aceitos[0];
          estado.fixes.push({
            severity: 'fix',
            message: `Opção não reconhecida em "${definicaoProp.label}" (${definicao.label}); o padrão foi aplicado.`,
            blockId: id,
          });
          continue;
        }
      }

      props[definicaoProp.key] = coagido;
    }

    // Propriedades fora do catálogo são descartadas (correção segura).
    const extras = Object.keys(propsEntrada).filter((chave) => !conhecidas.has(chave));
    if (extras.length) {
      estado.fixes.push({
        severity: 'fix',
        message: `Propriedades não reconhecidas em ${definicao.label} foram removidas: ${extras.join(', ')}.`,
        blockId: id,
      });
    }

    // ----------------------------------------------------------------- Filhos
    let filhos: StudioBlockNode[] | undefined;
    const filhosEntrada = (no.children ?? (no as Record<string, unknown>).nodes) as unknown;
    if (Array.isArray(filhosEntrada) && filhosEntrada.length) {
      if (!definicao.container) {
        estado.losses.push({
          severity: 'loss',
          message: `O bloco ${definicao.label} não aceita blocos dentro dele; o conteúdo interno seria perdido.`,
          blockId: id,
        });
      } else {
        filhos = normalizarNos(filhosEntrada, profundidade + 1, estado);
      }
    }

    // Metadados de composição (Fase 8) são preservados quando vierem no JSON:
    // não afetam o desenho e ajudam o Studio a avisar sobre espaço vazio.
    const meta: Partial<StudioBlockNode> = {};
    // Atenção: nome próprio (`cru`) porque `bruto` já é o item da repetição
    // acima — redeclarar aqui derrubava a leitura logo no primeiro bloco.
    const cru = no as Record<string, unknown>;
    if (typeof cru.slotId === 'string') meta.slotId = cru.slotId;
    if (typeof cru.slotRole === 'string') meta.slotRole = cru.slotRole as StudioBlockNode['slotRole'];
    if (typeof cru.sourceComposition === 'string') meta.sourceComposition = cru.sourceComposition;
    if (typeof cru.sourceCompositionVersion === 'number') {
      meta.sourceCompositionVersion = cru.sourceCompositionVersion;
    }

    saida.push(
      filhos?.length
        ? { id, type: tipo, props, children: filhos, ...meta }
        : { id, type: tipo, props, ...meta },
    );
  }

  return saida;
}

// =============================================================================
// Leitura do envelope
// =============================================================================

/** Tipos de documento aceitos na importação. */
const TIPOS_ACEITOS: StudioTemplateType[] = [
  'proposal',
  'online_proposal',
  'landing_page',
  'email',
  'report',
  'quote',
];

/**
 * Lê o texto colado, valida o envelope e devolve o documento normalizado
 * junto com o relatório de correções e perdas.
 */
export function parseStudioJsonDocument(texto: string): StudioJsonImportResult {
  const bruto = String(texto ?? '').trim();
  if (!bruto) throw new StudioJsonImportError('Cole o conteúdo em JSON antes de conferir.');

  if (new Blob([bruto]).size > STUDIO_JSON_LIMITS.maxPayloadBytes) {
    throw new StudioJsonImportError('O conteúdo colado é grande demais (limite de 1 MB).');
  }

  let objeto: unknown;
  try {
    objeto = JSON.parse(bruto);
  } catch {
    throw new StudioJsonImportError('O conteúdo não é um JSON válido. Verifique vírgulas e aspas.');
  }

  if (!objeto || typeof objeto !== 'object' || Array.isArray(objeto)) {
    throw new StudioJsonImportError('O JSON precisa ser um objeto com "schemaVersion" e "blocks".');
  }

  const envelope = objeto as Record<string, unknown>;

  // --------------------------------------------------------- Versão do formato
  const versaoBruta = envelope.schemaVersion ?? envelope.studio_schema ?? envelope.studioSchema;
  if (versaoBruta === undefined || versaoBruta === null || versaoBruta === '') {
    throw new StudioJsonImportError(
      'O arquivo não informa "schemaVersion". Inclua a versão do formato do Studio.',
    );
  }
  const versao = Number(String(versaoBruta).split('.')[0]);
  if (!Number.isFinite(versao)) {
    throw new StudioJsonImportError('O valor de "schemaVersion" é inválido.');
  }
  if (versao > STUDIO_SCHEMA_VERSION) {
    throw new StudioJsonImportError(
      `Este arquivo usa a versão ${versao} do formato e o sistema entende até a ${STUDIO_SCHEMA_VERSION}.`,
    );
  }

  // ------------------------------------------------------------- Tipo e origem
  const tipoBruto = String(envelope.documentType ?? envelope.document_type ?? '').trim();
  if (tipoBruto && !TIPOS_ACEITOS.includes(tipoBruto as StudioTemplateType)) {
    throw new StudioJsonImportError(`Tipo de documento não reconhecido: ${tipoBruto}.`);
  }

  const geradorBruto = envelope.generator as Record<string, unknown> | undefined;
  const generator: StudioImportGenerator | undefined = geradorBruto
    ? {
        name: geradorBruto.name ? String(geradorBruto.name).slice(0, 80) : undefined,
        version: geradorBruto.version ? String(geradorBruto.version).slice(0, 40) : undefined,
      }
    : undefined;

  // ------------------------------------------------------------------- Blocos
  const declarado = String(envelope.type ?? '').trim();
  const temNodes = Array.isArray(envelope.nodes);
  const kind: StudioImportKind =
    declarado === 'studio-fragment' || (temNodes && !Array.isArray(envelope.blocks))
      ? 'fragment'
      : 'document';

  const listaBruta = kind === 'fragment' ? envelope.nodes : envelope.blocks;
  if (!Array.isArray(listaBruta)) {
    throw new StudioJsonImportError('O arquivo precisa trazer a lista "blocks" com os blocos da página.');
  }

  const embutidas = contarImagensEmbutidas(listaBruta);
  if (embutidas > STUDIO_JSON_LIMITS.maxInlineImages) {
    throw new StudioJsonImportError(
      `O arquivo traz ${embutidas} imagens embutidas; o limite é ${STUDIO_JSON_LIMITS.maxInlineImages}.`,
    );
  }

  const estado: EstadoNormalizacao = {
    fixes: [],
    losses: [],
    idMap: {},
    usados: new Set<string>(),
    total: 0,
  };

  let blocos = normalizarNos(listaBruta, 1, estado);

  // Referências internas passam a apontar para os identificadores novos.
  if (Object.keys(estado.idMap).length) {
    blocos = reescreverReferencias(blocos, estado.idMap) as StudioBlockNode[];
  }

  const documento: StudioDocument = {
    schemaVersion: STUDIO_SCHEMA_VERSION,
    blocks: blocos,
    theme: (envelope.theme as StudioDocument['theme']) ?? {},
    settings: (envelope.settings as Record<string, unknown>) ?? {},
  };

  // Conferência final com a MESMA validação usada no restante do Studio.
  const estrutural = validateDocument(documento);
  estrutural.issues.forEach((problema) => {
    estado.losses.push({ severity: 'loss', message: problema.message, blockId: problema.blockId });
  });

  return {
    kind,
    document: documento,
    documentType: (tipoBruto || undefined) as StudioTemplateType | undefined,
    generator,
    validBlocks: estado.total,
    fixes: estado.fixes,
    losses: estado.losses,
    idMap: estado.idMap,
  };
}

/** Monta o texto de exportação do modelo aberto (exemplo para a IA). */
export function serializeStudioDocument(
  documento: StudioDocument,
  documentType: StudioTemplateType,
): string {
  return JSON.stringify(
    {
      schemaVersion: documento.schemaVersion ?? STUDIO_SCHEMA_VERSION,
      documentType,
      generator: { name: 'Uaizy Studio', version: 'export' },
      theme: documento.theme ?? {},
      settings: documento.settings ?? {},
      blocks: documento.blocks ?? [],
    },
    null,
    2,
  );
}
